import { createWorkshopRecordStore } from "@/lib/create-workshop-record-store";

/**
 * Decisions store — per-workshop log of facilitator-captured decisions
 * (the "why did we decide this?" record behind the Decision
 * Traceability non-negotiable). One of the four workshop-record stores
 * built on {@link createWorkshopRecordStore}; see that factory for the
 * shared persistence posture (IndexedDB by default; the BFF blob plane when
 * `VITE_WORKSHOP_BACKEND=bff`). A decision's status moves
 * proposed → agreed → parked over a session, so it uses `update()`.
 */

export type DecisionStatus = "proposed" | "agreed" | "parked";

export type Decision = {
  /** uuid-ish, unique within the workshop scope */
  id: string;
  /** Workshop scope. Stored on the entry too so the value survives a
   *  shape-shift without re-deriving from the bucket key. */
  workshopId: string;
  title: string;
  /** The "why" — free text rationale behind the decision. */
  rationale: string;
  /** Who owns / proposed it (display name). */
  owner: string;
  status: DecisionStatus;
  createdAt: number;
  addedBy: string;
  /** Free-form tags (P0.1 reserved seam). Optional; absent on legacy
   *  records, defaulted to `[]` at every read site. */
  tags?: string[];
};

/** Mutable fields a facilitator can edit after a decision is logged. */
export type DecisionPatch = Partial<
  Pick<Decision, "title" | "rationale" | "owner" | "status" | "tags">
>;

const STATUSES: DecisionStatus[] = ["proposed", "agreed", "parked"];

export const useDecisions = createWorkshopRecordStore<Decision, DecisionPatch>({
  idbKey: "xaes-decisions",
  recordType: "decisions",
  // Best-effort sanitiser: drop anything we can't recognise; unknown
  // status enums default to "proposed"; createdAt is preserved (falls back
  // to now only when missing so the list still sorts sanely).
  sanitiseItem: (d, fallbackWorkshopId) => {
    if (typeof d.id !== "string") return null;
    if (typeof d.title !== "string") return null;
    const status: DecisionStatus = STATUSES.includes(d.status as DecisionStatus)
      ? (d.status as DecisionStatus)
      : "proposed";
    return {
      id: d.id,
      workshopId:
        typeof d.workshopId === "string" ? d.workshopId : fallbackWorkshopId,
      title: d.title,
      rationale: typeof d.rationale === "string" ? d.rationale : "",
      owner: typeof d.owner === "string" ? d.owner : "",
      status,
      createdAt: typeof d.createdAt === "number" ? d.createdAt : Date.now(),
      addedBy: typeof d.addedBy === "string" ? d.addedBy : "",
      // P0.1 — preserve free-form tags when present; old blobs round-trip
      // cleanly (field stays absent). Array coerced + filtered to strings.
      tags: Array.isArray(d.tags)
        ? d.tags.filter((t): t is string => typeof t === "string")
        : undefined,
    };
  },
});
